import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  createClientPayment,
  deleteClientPayment,
  fetchClientPayments,
  fetchTochkaInbox,
  getApiErrorMessage,
  resolveTochkaInbox,
  updateClientFeeComment,
  updateClientPayment,
} from "../api";
import ErrorRetry from "../components/ErrorRetry";
import Loading from "../components/Loading";
import BankInbox from "./finance/BankInbox";

const CLIENT_INBOX_KINDS = ["service", "kontur", "dismiss"];

function toBankInboxItem(row) {
  return {
    id: row.id,
    direction: "credit",
    paid_at: row.paid_at,
    amount: row.amount,
    account_tail: null,
    owner_label: null,
    counterparty_name: row.payer_name,
    counterparty_inn: row.payer_inn,
    purpose: row.purpose,
    status: "unmatched",
    reason: row.reason,
    reason_label: row.reason_label,
    is_transfer: false,
    source: "tochka",
    client_id: row.client_id ?? null,
    client_name: row.client_name ?? null,
    expense_title: null,
    liability_title: null,
    employee_name: null,
  };
}

const STATUS_LABELS = {
  unpaid: "Не оплачен",
  overdue: "Просрочен",
  underpaid: "Недоплата",
  overpaid: "Переплата",
  paid: "Оплачен",
};

const STATUS_FILTERS = [
  { value: "", label: "Все" },
  { value: "overdue", label: "Просрочен" },
  { value: "underpaid", label: "Недоплата" },
  { value: "unpaid", label: "Не оплачен" },
  { value: "paid", label: "Оплачен" },
  { value: "overpaid", label: "Переплата" },
  { value: "no_fee", label: "Без суммы" },
  { value: "no_day", label: "Без дня" },
  { value: "paid_by_other", label: "Платит другой" },
];

const DATE_FORMAT = new Intl.DateTimeFormat("ru-RU", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

const AMOUNT_FORMAT = new Intl.NumberFormat("ru-RU", {
  style: "currency",
  currency: "RUB",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const MONTH_LABEL = new Intl.DateTimeFormat("ru-RU", {
  month: "long",
  year: "numeric",
});

const Icons = {
  Search: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="8"></circle>
      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
    </svg>
  ),
  Empty: () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="5" width="20" height="14" rx="2"></rect>
      <line x1="2" y1="10" x2="22" y2="10"></line>
    </svg>
  ),
  Bank: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 21h18" />
      <path d="M4 10h16" />
      <path d="M12 3l9 7H3l9-7z" />
      <path d="M6 10v11" />
      <path d="M10 10v11" />
      <path d="M14 10v11" />
      <path d="M18 10v11" />
    </svg>
  ),
};

function currentMonthIso() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function shiftMonth(month, delta) {
  const [year, monthNum] = String(month).split("-").map(Number);
  const date = new Date(year, monthNum - 1 + delta, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function formatMonthLabel(month) {
  const [year, monthNum] = String(month).split("-").map(Number);
  if (!year || !monthNum) return month;
  return MONTH_LABEL.format(new Date(year, monthNum - 1, 1));
}

function todayIso() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatDate(value) {
  if (!value) return "—";
  const [year, month, day] = String(value).split("-");
  if (!year || !month || !day) return value;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  if (Number.isNaN(date.getTime())) return value;
  return DATE_FORMAT.format(date);
}

function formatAmount(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return AMOUNT_FORMAT.format(num);
}

function dueLabel(day) {
  if (day == null) return "до конца месяца";
  return `до ${day}-го`;
}

function previewComment(value, max = 80) {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (!text) return "";
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

function parseOpenIds(raw) {
  return new Set(
    String(raw || "")
      .split(",")
      .map((value) => Number(value))
      .filter((id) => Number.isFinite(id) && id > 0)
  );
}

function patchParams(prev, patch) {
  const next = new URLSearchParams(prev);
  Object.entries(patch).forEach(([key, value]) => {
    if (value == null || value === "") next.delete(key);
    else next.set(key, String(value));
  });
  return next;
}

function emptyOverview() {
  return {
    month: currentMonthIso(),
    planned: 0,
    collected: 0,
    debt: 0,
    overdue_count: 0,
    unpaid_count: 0,
    paid_count: 0,
    no_fee_count: 0,
    no_day_count: 0,
    items: [],
  };
}

function PaymentForm({ form, setForm, disabled, submitLabel, onSubmit, onCancel, maxDate, idPrefix }) {
  const paidAtRef = useRef(null);
  const amountRef = useRef(null);
  const [localError, setLocalError] = useState("");
  const [errorField, setErrorField] = useState("");
  const paidAtErrorId = `${idPrefix}-paid-at-error`;
  const amountErrorId = `${idPrefix}-amount-error`;

  return (
    <form
      className="kp-pay-form"
      onSubmit={(e) => {
        e.preventDefault();
        const amount = Number(form.amount);
        if (!form.paid_at) {
          setLocalError("Укажите дату оплаты");
          setErrorField("paid_at");
          paidAtRef.current?.focus();
          return;
        }
        if (!Number.isFinite(amount) || amount <= 0) {
          setLocalError("Укажите сумму больше нуля");
          setErrorField("amount");
          amountRef.current?.focus();
          return;
        }
        if (form.paid_at > maxDate) {
          setLocalError("Дата оплаты не может быть в будущем");
          setErrorField("paid_at");
          paidAtRef.current?.focus();
          return;
        }
        setLocalError("");
        setErrorField("");
        onSubmit();
      }}
    >
      <label className="login-label">
        Дата оплаты
        <input
          ref={paidAtRef}
          className="login-input"
          type="date"
          name="paid_at"
          autoComplete="off"
          value={form.paid_at}
          onChange={(e) => setForm({ ...form, paid_at: e.target.value })}
          max={maxDate}
          required
          disabled={disabled}
          aria-invalid={errorField === "paid_at"}
          aria-describedby={errorField === "paid_at" ? paidAtErrorId : undefined}
        />
        {errorField === "paid_at" && localError && (
          <span className="cd-field-error" id={paidAtErrorId} role="status">{localError}</span>
        )}
      </label>
      <label className="login-label">
        Сумма,{"\u00A0"}₽
        <input
          ref={amountRef}
          className="login-input"
          type="number"
          name="amount"
          autoComplete="off"
          inputMode="decimal"
          min="0.01"
          step="0.01"
          value={form.amount}
          onChange={(e) => setForm({ ...form, amount: e.target.value })}
          required
          disabled={disabled}
          aria-invalid={errorField === "amount"}
          aria-describedby={errorField === "amount" ? amountErrorId : undefined}
        />
        {errorField === "amount" && localError && (
          <span className="cd-field-error" id={amountErrorId} role="status">{localError}</span>
        )}
      </label>
      <div className="kp-pay-actions">
        <button type="submit" className="login-button" disabled={disabled}>
          {submitLabel}
        </button>
        {onCancel && (
          <button type="button" className="cd-filter-reset" onClick={onCancel} disabled={disabled}>
            Отмена
          </button>
        )}
      </div>
    </form>
  );
}

function payerLabel(profile) {
  const inn = (profile.inn || "").trim();
  return inn ? `${profile.name} · ИНН ${inn}` : profile.name;
}

function ClientRow({
  item,
  month,
  expanded,
  onToggle,
  payForm,
  setPayForm,
  saving,
  onQuickPay,
  onCreatePayment,
  commentForm,
  setCommentForm,
  onSaveComment,
  editingId,
  editForm,
  setEditForm,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onAskDelete,
  onTogglePaidByOther,
  onToggleBarter,
  onSelectPayer,
  payerProfiles = [],
  payerPending,
}) {
  const hasFee = item.monthly_fee != null && Number(item.monthly_fee) > 0;
  const commentPreview = previewComment(item.comment);
  const paidByOther = Boolean(item.paid_by_other);
  const isBarter = Boolean(item.is_barter);
  const showPayerSelect = paidByOther || payerPending;
  const payerOptions = payerProfiles.filter((profile) => profile.id !== item.client_id);
  if (
    item.paid_by_client_id
    && !payerOptions.some((profile) => profile.id === item.paid_by_client_id)
  ) {
    payerOptions.unshift({
      id: item.paid_by_client_id,
      name: item.paid_by_client_name || `Клиент #${item.paid_by_client_id}`,
    });
  }
  const payerValue = item.paid_by_client_id ? String(item.paid_by_client_id) : "";
  return (
    <div className={`cd-row ${expanded ? "cd-row--expanded" : ""}`}>
      <div className="cp-row-head">
        <button
          type="button"
          className="cd-row-main"
          aria-expanded={expanded}
          aria-label={`${expanded ? "Свернуть" : "Раскрыть"} ${item.name}`}
          onClick={() => onToggle(item.client_id)}
        >
          <span className="cd-row-chevron" aria-hidden="true">{expanded ? "▾" : "▸"}</span>
          <div className="cd-row-body">
            <div className="cd-row-top">
              <span className="cd-row-name">{item.name}</span>
              <span className={`kp-badge kp-badge--${item.status}`}>
                {STATUS_LABELS[item.status] || item.status}
              </span>
              {item.current_payment?.from_bank ? (
                <span className="kp-badge kp-badge--bank">Банк</span>
              ) : null}
            </div>
            <div className="cd-row-meta">
              {item.monthly_fee == null ? "нет суммы" : formatAmount(item.monthly_fee) + "/мес"}
              {" · "}
              {dueLabel(item.payment_day)}
              {item.inn ? ` · ИНН ${item.inn}` : ""}
              {item.remaining > 0 ? ` · осталось ${formatAmount(item.remaining)}` : ""}
              {item.paid_by_client_name ? ` · платит ${item.paid_by_client_name}` : ""}
              {isBarter ? " · бартер" : ""}
              {commentPreview ? (
                <span className="cp-comment-preview">{commentPreview}</span>
              ) : null}
            </div>
          </div>
        </button>
        <div className="kp-other-payer">
          <label className="kp-other-payer-check">
            <input
              type="checkbox"
              name={`is_barter_${item.client_id}`}
              checked={isBarter}
              disabled={saving}
              aria-label={`${item.name}: бартер`}
              onChange={(e) => onToggleBarter(item, e.target.checked)}
            />
            Бартер
          </label>
          <label className="kp-other-payer-check">
            <input
              type="checkbox"
              name={`paid_by_other_${item.client_id}`}
              checked={paidByOther || payerPending}
              disabled={saving}
              aria-label={`${item.name}: платит другой клиент`}
              onChange={(e) => onTogglePaidByOther(item, e.target.checked)}
            />
            Платит другой клиент
          </label>
          {showPayerSelect && (
            <select
              className="login-input kp-other-payer-select"
              name={`paid_by_client_${item.client_id}`}
              value={payerValue}
              disabled={saving}
              aria-label={`${item.name}: кто платит`}
              onChange={(e) => {
                const raw = e.target.value;
                if (!raw) return;
                onSelectPayer(item, Number(raw));
              }}
            >
              <option value="">Выберите клиента</option>
              {payerOptions.map((profile) => (
                <option key={profile.id} value={profile.id}>
                  {payerLabel(profile)}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="cp-row-actions">
          <Link
            className="cp-dir-link"
            to={`/client-directory?card=${item.client_id}`}
            aria-label={`${item.name}: карточка`}
            onClick={(e) => e.stopPropagation()}
          >
            Карточка
          </Link>
          {!item.current_payment && !isBarter && (
            <button
              type="button"
              className="login-button cp-pay-btn"
              disabled={saving}
              onClick={() => {
                if (hasFee) onQuickPay(item);
                else onToggle(item.client_id, true);
              }}
            >
              Оплачено
            </button>
          )}
        </div>
      </div>
      {expanded && (
        <div className="cd-row-expand kp-expand">
          <div className="kp-add-block">
            <h3 className="kp-history-title">Комментарий</h3>
            <form
              className="cp-comment-form"
              onSubmit={(e) => {
                e.preventDefault();
                onSaveComment(item.client_id);
              }}
            >
              <label className="login-label">
                Комментарий
                <textarea
                  className="login-input"
                  name="comment"
                  autoComplete="off"
                  rows={3}
                  maxLength={1000}
                  value={commentForm}
                  onChange={(e) => setCommentForm(e.target.value)}
                  disabled={saving}
                />
              </label>
              <div className="kp-pay-actions">
                <button type="submit" className="login-button" disabled={saving}>
                  Сохранить
                </button>
              </div>
            </form>
          </div>
          {!item.current_payment && !isBarter && (
            <div className="kp-add-block">
              <h3 className="kp-history-title">
                {hasFee ? "Отметить оплату" : "Укажите сумму оплаты"}
              </h3>
              <PaymentForm
                form={payForm}
                setForm={setPayForm}
                disabled={saving}
                submitLabel="Записать оплату"
                onSubmit={() => onCreatePayment(item.client_id)}
                maxDate={todayIso()}
                idPrefix={`pay-${item.client_id}`}
              />
            </div>
          )}
          <div className="kp-history">
            <h3 className="kp-history-title">История оплат</h3>
            {item.payments.length === 0 ? (
              <p className="kp-history-empty">Оплат ещё нет</p>
            ) : (
              <ul className="kp-history-list">
                {item.payments.map((payment) => (
                  <li key={payment.id} className="kp-history-item">
                    {editingId === payment.id ? (
                      <PaymentForm
                        form={editForm}
                        setForm={setEditForm}
                        disabled={saving}
                        submitLabel="Сохранить оплату"
                        onSubmit={() => onSaveEdit(payment.id)}
                        onCancel={onCancelEdit}
                        maxDate={todayIso()}
                        idPrefix={`edit-${payment.id}`}
                      />
                    ) : (
                      <>
                        <div className="kp-history-main">
                          <span>
                            {payment.period === month ? "Этот месяц" : payment.period}
                            {" · "}
                            {formatDate(payment.paid_at)}
                            {payment.from_bank ? " · Банк" : ""}
                          </span>
                          <span>{formatAmount(payment.amount)}</span>
                        </div>
                        <div className="kp-history-actions">
                          <button
                            type="button"
                            className="cd-sort-btn"
                            onClick={() => onStartEdit(payment)}
                          >
                            Изменить
                          </button>
                          <button
                            type="button"
                            className="cd-danger-btn"
                            onClick={() => onAskDelete(payment)}
                          >
                            Удалить
                          </button>
                        </div>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ClientPayments() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const statusFilter = searchParams.get("status") || "";
  const month = searchParams.get("month") || currentMonthIso();
  const expanded = useMemo(() => parseOpenIds(searchParams.get("open")), [searchParams]);
  const updateParams = useCallback((patch) => {
    setSearchParams((prev) => patchParams(prev, patch), { replace: true });
  }, [setSearchParams]);

  const [overview, setOverview] = useState(emptyOverview);
  const [inbox, setInbox] = useState([]);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [saving, setSaving] = useState(false);
  const [payForms, setPayForms] = useState({});
  const [commentForms, setCommentForms] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ paid_at: todayIso(), amount: "" });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [pendingPayer, setPendingPayer] = useState(() => new Set());

  const loadItems = useCallback(async ({ soft = false } = {}) => {
    if (!soft) setLoading(true);
    setError("");
    try {
      const data = await fetchClientPayments({ month });
      setOverview(data);
      try {
        const incoming = await fetchTochkaInbox();
        setInbox(Array.isArray(incoming?.items) ? incoming.items : []);
      } catch {
        setInbox([]);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, "Не удалось загрузить оплаты клиентов"));
    } finally {
      if (!soft) setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const setPayForm = (clientId, form) => {
    setPayForms((prev) => ({ ...prev, [clientId]: form }));
  };

  const setCommentForm = (clientId, value) => {
    setCommentForms((prev) => ({ ...prev, [clientId]: value }));
  };

  const defaultPayForm = (item) => ({
    paid_at: todayIso(),
    amount: item.monthly_fee != null && Number(item.monthly_fee) > 0 ? String(item.monthly_fee) : "",
  });

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return overview.items.filter((item) => {
      const paidByOther = Boolean(item.paid_by_other);
      const pending = pendingPayer.has(item.client_id);
      if (statusFilter === "paid_by_other") {
        if (!paidByOther && !pending) return false;
      } else if (paidByOther && !pending) {
        return false;
      } else if (statusFilter === "no_fee" && item.monthly_fee != null) {
        return false;
      } else if (statusFilter === "no_day" && item.payment_day != null) {
        return false;
      } else if (statusFilter && statusFilter !== "no_fee" && statusFilter !== "no_day" && item.status !== statusFilter) {
        return false;
      }
      if (!needle) return true;
      const inn = (item.inn || "").toLowerCase();
      const comment = (item.comment || "").toLowerCase();
      const payerName = (item.paid_by_client_name || "").toLowerCase();
      return (
        item.name.toLowerCase().includes(needle)
        || inn.includes(needle)
        || comment.includes(needle)
        || payerName.includes(needle)
      );
    });
  }, [overview.items, query, statusFilter, pendingPayer]);

  const payerProfiles = useMemo(
    () => overview.items.map((row) => ({
      id: row.client_id,
      name: row.name,
      inn: row.inn,
    })),
    [overview.items]
  );

  const inboxItems = useMemo(() => inbox.map(toBankInboxItem), [inbox]);
  const inboxClients = useMemo(
    () => overview.items.map((row) => ({ id: row.client_id, name: row.name })),
    [overview.items]
  );

  const handleInboxResolve = async (id, body) => {
    setActionError("");
    try {
      await resolveTochkaInbox(id, body);
      await loadItems({ soft: true });
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось разобрать поступление"));
      throw err;
    }
  };

  const toggleExpanded = (id, forceOpen = false) => {
    const next = new Set(expanded);
    if (forceOpen) next.add(id);
    else if (next.has(id)) next.delete(id);
    else next.add(id);
    updateParams({ open: [...next].join(",") });
  };

  const handleQuickPay = async (item) => {
    setSaving(true);
    setActionError("");
    try {
      await createClientPayment({ client_id: item.client_id, period: month });
      await loadItems({ soft: true });
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось записать оплату"));
    } finally {
      setSaving(false);
    }
  };

  const handleCreatePayment = async (clientId) => {
    const form = payForms[clientId] || { paid_at: todayIso(), amount: "" };
    setSaving(true);
    setActionError("");
    try {
      await createClientPayment({
        client_id: clientId,
        period: month,
        paid_at: form.paid_at,
        amount: Number(form.amount),
      });
      await loadItems({ soft: true });
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось записать оплату"));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveComment = async (clientId) => {
    const raw = commentForms[clientId];
    const item = overview.items.find((row) => row.client_id === clientId);
    const comment = raw == null ? (item?.comment || "") : raw;
    setSaving(true);
    setActionError("");
    try {
      await updateClientFeeComment(clientId, { comment });
      setCommentForms((prev) => {
        const next = { ...prev };
        delete next[clientId];
        return next;
      });
      await loadItems({ soft: true });
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось сохранить комментарий"));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleBarter = async (item, isBarter) => {
    setSaving(true);
    setActionError("");
    try {
      await updateClientFeeComment(item.client_id, { is_barter: isBarter });
      await loadItems({ soft: true });
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось сохранить признак бартера"));
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePaidByOther = async (item, paidByOther) => {
    if (paidByOther) {
      setActionError("");
      setPendingPayer((prev) => {
        const next = new Set(prev);
        next.add(item.client_id);
        return next;
      });
      return;
    }
    setPendingPayer((prev) => {
      const next = new Set(prev);
      next.delete(item.client_id);
      return next;
    });
    if (!item.paid_by_other) return;
    setSaving(true);
    setActionError("");
    try {
      await updateClientFeeComment(item.client_id, { paid_by_other: false });
      await loadItems({ soft: true });
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось сохранить признак оплаты"));
    } finally {
      setSaving(false);
    }
  };

  const handleSelectPayer = async (item, payerId) => {
    setSaving(true);
    setActionError("");
    try {
      await updateClientFeeComment(item.client_id, {
        paid_by_other: true,
        paid_by_client_id: payerId,
      });
      setPendingPayer((prev) => {
        const next = new Set(prev);
        next.delete(item.client_id);
        return next;
      });
      await loadItems({ soft: true });
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось сохранить плательщика"));
    } finally {
      setSaving(false);
    }
  };

  const handleStartEdit = (payment) => {
    setEditingId(payment.id);
    setEditForm({
      paid_at: payment.paid_at,
      amount: String(payment.amount),
    });
  };

  const handleSaveEdit = async (paymentId) => {
    setSaving(true);
    setActionError("");
    try {
      await updateClientPayment(paymentId, {
        paid_at: editForm.paid_at,
        amount: Number(editForm.amount),
      });
      setEditingId(null);
      await loadItems({ soft: true });
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось изменить оплату"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setActionError("");
    try {
      await deleteClientPayment(deleteTarget.id);
      setDeleteTarget(null);
      await loadItems({ soft: true });
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось удалить оплату"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-container cd-page kp-page">
      <div className="page-header">
        <h2>Оплаты клиентов</h2>
        <p className="page-subtitle">Ежемесячные оплаты за услуги</p>
      </div>

      {error && <ErrorRetry message={error} onRetry={() => loadItems()} />}
      {actionError && !deleteTarget && (
        <div className="login-error" role="status" aria-live="polite">{actionError}</div>
      )}

      <div className="cp-month-nav" role="group" aria-label="Месяц">
        <button
          type="button"
          className="cd-sort-btn"
          onClick={() => updateParams({ month: shiftMonth(month, -1) })}
        >
          ←
        </button>
        <div className="cp-month-label">{formatMonthLabel(month)}</div>
        <button
          type="button"
          className="cd-sort-btn"
          onClick={() => updateParams({ month: shiftMonth(month, 1) })}
        >
          →
        </button>
      </div>

      <div className="cd-stats">
        <div className="cd-stat">План <strong>{formatAmount(overview.planned)}</strong></div>
        <div className="cd-stat">Собрано <strong>{formatAmount(overview.collected)}</strong></div>
        <div className="cd-stat">Долг <strong>{formatAmount(overview.debt)}</strong></div>
        <div className="cd-stat">Просрочено <strong>{overview.overdue_count}</strong></div>
      </div>

      {inbox.length > 0 && (
        <section
          className={`cp-inbox ${inboxOpen ? "cp-inbox--open" : ""}`}
          aria-label="Неразобранные поступления"
        >
          <button
            type="button"
            className="cp-inbox-toggle"
            aria-expanded={inboxOpen}
            aria-controls="cp-inbox-list"
            onClick={() => setInboxOpen((v) => !v)}
          >
            <span className="cd-row-chevron" aria-hidden="true">{inboxOpen ? "▾" : "▸"}</span>
            <span className="cp-inbox-icon" aria-hidden="true"><Icons.Bank /></span>
            <span className="cp-inbox-title">Не разобрано из банка</span>
            <span className="cp-inbox-count">{inbox.length}</span>
          </button>
          {inboxOpen && (
            <>
              <p className="cp-inbox-note">
                Эти поступления не записали автоматически. Оплаты в журнале не менялись.
              </p>
              <div id="cp-inbox-list">
                <BankInbox
                  items={inboxItems}
                  kinds={CLIENT_INBOX_KINDS}
                  options={{ clients: inboxClients, expenses: [], liabilities: [], employees: [] }}
                  onResolve={handleInboxResolve}
                  title="Не разобрано из банка"
                />
              </div>
            </>
          )}
        </section>
      )}

      <div className="cd-toolbar">
        <div className="cd-search">
          <Icons.Search />
          <input
            type="search"
            name="q"
            autoComplete="off"
            aria-label="Поиск по названию, ИНН, комментарию или плательщику"
            placeholder="Название, ИНН, комментарий, кто платит…"
            value={query}
            onChange={(e) => updateParams({ q: e.target.value })}
          />
        </div>
      </div>

      <div className="kp-chips" role="group" aria-label="Фильтр по статусу">
        {STATUS_FILTERS.map((chip) => (
          <button
            key={chip.value || "all"}
            type="button"
            className={`kp-chip ${statusFilter === chip.value ? "kp-chip--active" : ""}`}
            aria-pressed={statusFilter === chip.value}
            onClick={() => updateParams({ status: chip.value })}
          >
            {chip.label}
          </button>
        ))}
      </div>

      {loading ? (
        <Loading />
      ) : visible.length === 0 ? (
        <div className="cd-empty">
          <Icons.Empty />
          <p>
            {overview.items.length === 0
              ? "Нет активных клиентов. Добавьте клиента в справочнике."
              : "Ничего не найдено по текущему фильтру"}
          </p>
        </div>
      ) : (
        <ul className="cd-list">
          {visible.map((item) => (
            <li key={item.client_id}>
              <ClientRow
                item={item}
                month={month}
                expanded={expanded.has(item.client_id)}
                onToggle={toggleExpanded}
                payForm={payForms[item.client_id] || defaultPayForm(item)}
                setPayForm={(form) => setPayForm(item.client_id, form)}
                saving={saving}
                onQuickPay={handleQuickPay}
                onCreatePayment={handleCreatePayment}
                commentForm={commentForms[item.client_id] ?? (item.comment || "")}
                setCommentForm={(value) => setCommentForm(item.client_id, value)}
                onSaveComment={handleSaveComment}
                editingId={editingId}
                editForm={editForm}
                setEditForm={setEditForm}
                onStartEdit={handleStartEdit}
                onCancelEdit={() => setEditingId(null)}
                onSaveEdit={handleSaveEdit}
                onAskDelete={setDeleteTarget}
                onTogglePaidByOther={handleTogglePaidByOther}
                onToggleBarter={handleToggleBarter}
                onSelectPayer={handleSelectPayer}
                payerProfiles={payerProfiles}
                payerPending={pendingPayer.has(item.client_id)}
              />
            </li>
          ))}
        </ul>
      )}

      {deleteTarget && (
        <div className="users-modal-backdrop" onClick={() => setDeleteTarget(null)}>
          <div
            className="users-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-del-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cp-del-title">Удалить оплату?</h3>
            <p>
              {deleteTarget.period} · {formatDate(deleteTarget.paid_at)} · {formatAmount(deleteTarget.amount)}
            </p>
            {actionError && <div className="login-error" role="status" aria-live="polite">{actionError}</div>}
            <div className="users-modal-actions">
              <button type="button" className="cd-filter-reset" onClick={() => setDeleteTarget(null)}>
                Отмена
              </button>
              <button
                type="button"
                className="cd-danger-btn"
                disabled={saving}
                onClick={handleDelete}
              >
                Удалить оплату
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
